/**
 * OPARA v7.0 - Language Gateway (Splash Screen)
 * 
 * Premium full-screen language selection interface.
 * 2x3 Grid layout with equal-sized tiles for all 6 languages.
 */

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Globe } from 'lucide-react';
import Footer from './Footer';

const LanguageGateway = ({ forceShow = false }) => {
  const { showGateway, selectLanguage, supportedLanguages, isInitialized } = useLanguage();

  // Show if forceShow is true OR if context says to show
  if (!forceShow && !showGateway) return null;
  
  // Wait for initialization to avoid flash
  if (!isInitialized && !forceShow) return null;

  // Define the grid order: EN, ES, FR (row 1) | DE, TR, AR (row 2)
  const gridOrder = ['en', 'es', 'fr', 'de', 'tr', 'ar'];
  const orderedLanguages = gridOrder.map(code => supportedLanguages[code]).filter(Boolean);

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black" />
      
      {/* Main Content - Centered */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="relative z-10 w-full max-w-2xl mx-auto text-center">
          {/* Logo */}
          <div className="mb-12">
            <img 
              src="/opara-logo-trimmed.png" 
              alt="OPARA" 
              className="h-14 w-auto mx-auto mb-5"
            />
            <div className="flex items-center justify-center gap-2 text-gray-400">
              <Globe className="w-4 h-4" />
              <span className="text-sm tracking-widest uppercase">Select Your Language</span>
            </div>
          </div>

          {/* 2x3 Language Grid - Equal Tiles */}
          <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto">
            {orderedLanguages.map((lang) => {
              const isActive = lang.status === 'production' || lang.status === 'master';
              
              return (
                <button
                  key={lang.code}
                  onClick={() => selectLanguage(lang.code)}
                  className={`
                    group relative rounded-2xl p-5 transition-all duration-300 border
                    ${isActive 
                      ? 'bg-gray-900/80 border-gray-700 hover:border-[#FFDD59]/50 hover:bg-gray-800/80' 
                      : 'bg-gray-900/30 border-gray-800/50 opacity-50 hover:opacity-70'
                    }
                  `}
                  data-testid={`lang-select-${lang.code}`}
                >
                  {/* Hover glow for active languages */}
                  {isActive && (
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#FFDD59]/0 to-[#FFDD59]/0 group-hover:from-[#FFDD59]/5 group-hover:to-[#FFDD59]/10 transition-all duration-300" />
                  )}
                  
                  {/* Coming Soon Badge - Top right corner */}
                  {!isActive && (
                    <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-gray-800/50 rounded text-[9px] text-gray-500 font-light tracking-wide">
                      Soon
                    </div>
                  )}
                  
                  <div className="relative flex flex-col items-center">
                    {/* Circular Flag - Minimal */}
                    <div className={`
                      w-11 h-11 mb-3 rounded-full flex items-center justify-center text-xl
                      transition-all duration-300
                      ${isActive 
                        ? 'bg-gray-800 border border-gray-600 group-hover:border-[#FFDD59]/40' 
                        : 'bg-gray-800/30 border border-gray-700/30'
                      }
                    `}>
                      {lang.flag}
                    </div>
                    
                    {/* Native Name */}
                    <div className={`
                      text-base font-semibold
                      ${isActive ? 'text-white' : 'text-gray-500'}
                      ${lang.dir === 'rtl' ? 'font-arabic' : ''}
                    `}>
                      {lang.nativeName}
                    </div>
                    
                    {/* Active indicator - subtle */}
                    {isActive && (
                      <div className="w-1 h-1 mt-2 rounded-full bg-[#FFDD59]/60" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Tagline */}
          <p className="text-gray-600 text-xs mt-12 tracking-wide">
            The Science of Influencer ROI on Instagram
          </p>
        </div>
      </div>
      
      {/* Footer */}
      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  );
};

export default LanguageGateway;
